// -----------------------------------------------------------------------------
// src/components/NavBar/CreatorNavbar.js
// Drawer anchors LEFT on desktop, RIGHT on mobile
// -----------------------------------------------------------------------------
import React, { useMemo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "react-query";
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  Divider,
  IconButton,
  styled,
  ListItemIcon,
  ListItemText,
  Tooltip,
  ListItemButton,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Toolbar,
  ListItemAvatar,
} from "@mui/material";

import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LiveHelpOutlinedIcon from "@mui/icons-material/LiveHelpOutlined";
import AppsIcon from "@mui/icons-material/Apps";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import StarIcon from "@mui/icons-material/Star";
import LogoutIcon from "@mui/icons-material/Logout";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import HandshakeOutlined from "@mui/icons-material/HandshakeOutlined";

import { useCreatorAuth } from "../Hooks/creator-use-auth";
import ListItemLink from "./ListItemLink";
import routes from "../Config/routes";
import API from "../API";
import { drawerWidth } from "../Utils/constants";

/* —————————————————— Drawer mixins —————————————————— */
const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});
const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(9)} - 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(10)} - 1px)`,
  },
});
const IconDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "extended",
})(({ theme, extended }) => ({
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(extended
    ? {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      }
    : {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      }),
}));

/* —————————————————— Safe-area helpers (dynamic) —————————————————— */
const buildSafeAreaInset = (anchor) => ({
  pt: { xs: "env(safe-area-inset-top)", md: 0 },
  pb: { xs: "env(safe-area-inset-bottom)", md: 0 },
  pl: anchor === "left" ? { xs: "env(safe-area-inset-left)", md: 0 } : 0,
  pr: anchor === "right" ? { xs: "env(safe-area-inset-right)", md: 0 } : 0,
});

export default function CreatorNavbar({
  isDesktop = true,
  isOpen = true,
  onClose,
  onToggleExtend,
  onUnreadCountChange,
}) {
  const { creatorToken, logout } = useCreatorAuth();
  const navigate = useNavigate();
  const isExtended = !isDesktop || isOpen;

  /* —— NEW: determine anchor based on screen size —— */
  const anchor = isDesktop ? "left" : "right";

  /* ——— 1) CHAT UNREAD COUNT ——— */
  const { data: conversations = [] } = useQuery(
    ["creatorConversations", creatorToken?.creator_user?.username],
    API.conversations.fetchCreatorConversations,
    {
      enabled: !!creatorToken,
      staleTime: 1000 * 30,
      refetchOnWindowFocus: false,
    }
  );
  const unreadChatCount = useMemo(
    () => conversations.reduce((acc, c) => acc + (Number(c.unread_count) || 0), 0),
    [conversations]
  );

  /* ——— 2) NOTIFICATION COUNTS ——— */
  const {
    data: unreadNotifCountData,
    isLoading: notifCountLoading,
    refetch: refetchUnreadNotif,
  } = useQuery(
    ["creatorNotifications", "unread_count"],
    () => API.notifications.creatorGetUnreadCount(),
    {
      enabled: !!creatorToken,
      staleTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
    }
  );
  const unreadNotifCount = unreadNotifCountData?.unread_count || 0;

  const { data: unreadCollabData, refetch: refetchUnreadCollabs } = useQuery(
    ["creatorNotifications", "unread_collab_count"],
    () => API.notifications.creatorGetUnreadCollabCount(),
    {
      enabled: !!creatorToken,
      staleTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
    }
  );
  const unreadCollabCount = unreadCollabData?.unread_collab_count || 0;

  /* ——— 3) NOTIFICATION LIST / MENU ——— */
  const { data: notificationsList, refetch: refetchNotificationsList } = useQuery(
    ["creatorNotifications", "list_all"],
    () => API.notifications.creatorListAll(false),
    { enabled: false }
  );

  const markReadMutation = useMutation((id) => API.notifications.creatorMarkAsRead(id), {
    onSuccess: () => {
      refetchNotificationsList();
      refetchUnreadNotif();
      refetchUnreadCollabs();
    },
  });

  const [notifAnchor, setNotifAnchor] = useState(null);
  const notifMenuOpen = Boolean(notifAnchor);
  const handleOpenNotifications = (e) => {
    setNotifAnchor(e.currentTarget);
    refetchNotificationsList();
  };
  const handleCloseNotifications = () => setNotifAnchor(null);
  const handleNotifClick = (n) => {
    markReadMutation.mutate(n.id);
    if (n.url?.trim()) /* navigate(n.url);*/
    handleCloseNotifications();
  };

  /* ——— 4) PROPAGATE UNREAD TO HEADER BADGE ——— */
  const totalMobileBadge = unreadChatCount + unreadNotifCount;
  useEffect(() => {
    onUnreadCountChange?.(totalMobileBadge);
  }, [totalMobileBadge, onUnreadCountChange]);

  /* ——— 5) PARTNER / AGENCY LOGIC ——— */
  const agency = creatorToken?.creator_user?.agency;
  const isAgencyRestricted = (a) => a && a !== "TCC";

  /* ——— 6) NAV LINKS (updated) ——— */
  const links = [
    { text: "Dashboard", route: routes.creatorConnectStart, icon: <DashboardIcon /> },
    { text: "Money", route: routes.creatorConnectPayoutsInvoicing, icon: <AttachMoneyIcon /> },
    {
      text: "Deals",
      route: routes.creatorCollabs,
      icon: (
        <Badge badgeContent={unreadCollabCount} color="secondary" max={999} invisible={!unreadCollabCount}>
          <HandshakeOutlined />
        </Badge>
      ),
    },
    {
      text: "Chat",
      route: routes.creatorConvos,
      icon: (
        <Badge badgeContent={unreadChatCount} color="primary" max={999} invisible={!unreadChatCount}>
          <ChatBubbleOutlineIcon />
        </Badge>
      ),
    },
    { text: "Bio", route: routes.creatorbio, icon: <AccountCircleIcon /> },
    { text: "Partner Suite", route: routes.partnerSuite, icon: <StarIcon /> },
    { text: "Apps", route: routes.creatorConnectApps, icon: <AppsIcon /> },
  ];
  const filteredLinks = links.filter(
    (item) => !((item.text === "Deals" || item.text === "Sales") && isAgencyRestricted(agency))
  );

  /* ——— 7) BASIC PROFILE INFO ——— */
  const creator = creatorToken?.creator_user?.creator;
  const displayName = creator?.display_name || creatorToken?.creator_user?.username;
  const status = creator?.status || "";
  const photo = creator?.pfphref;

  /* ——— 8) RENDER ——— */
  return (
    <IconDrawer
      anchor={anchor}
      variant={isDesktop ? "permanent" : "temporary"}
      open={isDesktop || isOpen}
      extended={isExtended}
      onClose={onClose}
      sx={{
        position: "relative",
        zIndex: (theme) => (isDesktop ? theme.zIndex.appBar - 1 : theme.zIndex.drawer),
        color: "#000",
        "& .MuiDrawer-paper": {
          color: "#000",
          backgroundColor: "#FFF",
          overflowY: { xs: "auto", md: "hidden" },
          boxSizing: "border-box",
          ...buildSafeAreaInset(anchor),
        },
      }}
    >
      {/* Reserve toolbar height on desktop */}
      {isDesktop && <Toolbar />}

      {/* —— Profile —— */}
      <Box sx={{ p: 2, display: "flex", alignItems: "center" }}>
        <Avatar
          src={photo}
          alt={displayName}
          sx={{ width: 48, height: 48, cursor: !isExtended ? "pointer" : "default" }}
          onClick={() => !isExtended && onToggleExtend?.()}
        />
        {isExtended && (
          <Box sx={{ ml: 2, flex: 1 }}>
            <Typography variant="subtitle1">
              <b>{displayName}</b>
            </Typography>
            {status && <Typography variant="caption">{status}</Typography>}
            <Box sx={{ mt: 1, display: "flex", alignItems: "center" }}>
              <Tooltip title="Notifications" placement="right">
                <IconButton color="inherit" sx={{ mr: 1 }} onClick={handleOpenNotifications}>
                  <Badge badgeContent={notifCountLoading ? 0 : unreadNotifCount} color="secondary">
                    <NotificationsNoneIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
              <Tooltip title="Account settings" placement="right">
                <IconButton onClick={() => navigate(routes.creatorConnectEdit)} color="inherit" sx={{ mr: 1 }}>
                  <SettingsOutlinedIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Log out" placement="right">
                <IconButton
                  onClick={() => {
                    logout();
                    navigate(routes.home);
                  }}
                  color="inherit"
                >
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
      </Box>
      <Divider />

      {/* —— Main Links —— */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        <Box sx={{ overflowY: isExtended ? "auto" : "clip", flexGrow: 1 }}>
          <List>
            {filteredLinks.map((item) => (
              <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
                <ListItemLink to={item.route} primary={item.text} icon={item.icon} isOpen={isOpen} />
              </ListItem>
            ))}
          </List>
        </Box>

        <Box>
          <Divider />
          <ListItem disablePadding>
            <ListItemButton onClick={() => navigate("/help")}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <LiveHelpOutlinedIcon />
              </ListItemIcon>
              <ListItemText primary="Help" sx={{ opacity: isExtended ? 1 : 0 }} />
            </ListItemButton>
          </ListItem>
        </Box>
      </Box>

      {/* —— Notification Menu —— */}
      <Menu
        anchorEl={notifAnchor}
        open={notifMenuOpen}
        onClose={handleCloseNotifications}
        anchorOrigin={{ vertical: "bottom", horizontal: anchor }}
        transformOrigin={{ vertical: "top", horizontal: anchor }}
      >
        {!notificationsList?.length && <MenuItem>No notifications</MenuItem>}
        {notificationsList?.map((n) => {
          const isUnread = !n.read_at;
          return (
            <MenuItem
              key={n.id}
              onClick={() => handleNotifClick(n)}
              sx={{ backgroundColor: isUnread ? "#f2f2f2" : "#fff" }}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: "primary.main", width: 28, height: 28 }}>
                  <NotificationsNoneIcon fontSize="small" />
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary={n.purpose} secondary={n.message} />
            </MenuItem>
          );
        })}
      </Menu>
    </IconDrawer>
  );
}
